// src/contexts/AuthContext.jsx
// Single source of auth state — one instance, one onAuthStateChange listener.
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*, municipalities(*)')
        .eq('auth_id', userId)
        .maybeSingle();
      if (error) {
        console.error('Error fetching profile:', error);
        setProfile(null);
      } else {
        setProfile(data);
      }
    } catch (err) {
      console.error('Profile fetch failed:', err);
      setProfile(null);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((resolve) =>
          setTimeout(() => resolve({ data: { session: null }, timedOut: true }), 8000)
        );

        const result = await Promise.race([sessionPromise, timeoutPromise]);
        if (!mounted) return;

        let session = result?.data?.session;
        if (!session && result?.timedOut) {
          console.warn('getSession timed out, checking localStorage...');
          const stored = localStorage.getItem('padd-cdbg-hub-auth');
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              if (parsed?.access_token) {
                const { data } = await supabase.auth.setSession({
                  access_token: parsed.access_token,
                  refresh_token: parsed.refresh_token,
                });
                session = data?.session;
              }
            } catch (e) {
              console.error('Failed to restore session from storage:', e);
            }
          }
        }

        if (!mounted) return;
        setUser(session?.user ?? null);
        if (session?.user) await fetchProfile(session.user.id);
      } catch (err) {
        console.error('Auth init failed:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (!mounted) return;
        setUser(session?.user ?? null);
        if (session?.user) await fetchProfile(session.user.id);
        else setProfile(null);
        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signUp = async (email, password, fullName, municipalityId, role = 'clerk') => {
    const { data: authData, error: authError } = await supabase.auth.signUp({ email, password });
    if (authError) return { error: authError };
    const { error: profileError } = await supabase.from('users').insert({
      auth_id: authData.user.id,
      email,
      name: fullName,
      municipality_id: municipalityId || null,
      role,
    });
    if (profileError) return { error: profileError };
    return { data: authData };
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setUser(null);
      setProfile(null);
    }
    return { error };
  };

  const value = {
    user,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    isAuthenticated: !!user,
    isStaff: profile?.role === 'staff',
    isClerk: profile?.role === 'clerk',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used inside <AuthProvider>');
  return ctx;
}
